import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { episodes } from '@/mocks/episodes';
import EpisodeCard from '@/components/feature/EpisodeCard';
import { usePageMeta } from '@/hooks/usePageMeta';
import CategoryControls, { type SortKey, type StatusKey } from './components/CategoryControls';

const PAGE_SIZE = 6;

export default function Category() {
  usePageMeta({
    title: '无颜之月第一季全集剧集目录 - 无颜之月动漫视频平台',
    description:
      '无颜之月第一季全集剧集目录，收录第一季全部剧集，支持集数与更新时间排序、状态筛选，快速查看每集剧情介绍与播放入口。',
    keywords: '无颜之月剧集目录,动漫第一季全集,无颜之月动漫,剧集分类,动漫视频平台',
  });

  const [sortBy, setSortBy] = useState<SortKey>('episode-asc');
  const [status, setStatus] = useState<StatusKey>('all');
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const list = episodes.filter((ep) => (status === 'all' ? true : ep.status === status));
    const sorted = [...list].sort((a, b) => {
      switch (sortBy) {
        case 'episode-asc':
          return a.number - b.number;
        case 'episode-desc':
          return b.number - a.number;
        case 'time-desc':
          return new Date(b.releaseDate).getTime() - new Date(a.releaseDate).getTime();
        case 'time-asc':
          return new Date(a.releaseDate).getTime() - new Date(b.releaseDate).getTime();
        default:
          return 0;
      }
    });
    return sorted;
  }, [sortBy, status]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const handleSort = (value: SortKey) => {
    setSortBy(value);
    setPage(1);
  };
  const handleStatus = (value: StatusKey) => {
    setStatus(value);
    setPage(1);
  };
  const resetFilters = () => {
    setSortBy('episode-asc');
    setStatus('all');
    setPage(1);
  };

  return (
    <div className="w-full px-4 md:px-8 pt-24 md:pt-28">
      <nav className="flex items-center flex-wrap gap-1 text-xs text-foreground-500 mb-6">
        <Link to="/" className="hover:text-accent-400 transition-colors">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-300">剧集分类</span>
      </nav>

      <div className="mb-8">
        <h1 className="font-heading font-bold text-2xl md:text-4xl text-foreground-50">
          无颜之月第一季全剧集目录
        </h1>
        <p className="mt-3 text-sm md:text-base text-foreground-400 max-w-3xl leading-relaxed">
          这里收录了「无颜之月」动画第一季的全部剧集。你可以按集数或更新时间排序，并筛选已更新 / 待更新状态，快速定位想看的剧情。
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6 lg:gap-8 items-start">
        <aside className="lg:sticky lg:top-24">
          <CategoryControls
            sortBy={sortBy}
            status={status}
            resultCount={filtered.length}
            onSortChange={handleSort}
            onStatusChange={handleStatus}
          />
        </aside>

        <div>
          {paged.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-lg bg-background-900 border border-primary-900/40 py-20 text-center">
              <span className="w-16 h-16 flex items-center justify-center rounded-full bg-primary-500/15 text-primary-300 mb-4">
                <i className="ri-emotion-sad-line text-3xl"></i>
              </span>
              <h3 className="font-heading text-lg font-semibold text-foreground-100">暂无符合条件的剧集</h3>
              <p className="mt-2 text-sm text-foreground-400">换个筛选条件试试，或重置后查看全部剧集。</p>
              <button
                type="button"
                onClick={resetFilters}
                className="mt-6 inline-flex items-center gap-2 rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-background-950 hover:bg-accent-400 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-refresh-line"></i>
                重置筛选
              </button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-5">
                {paged.map((episode) => (
                  <EpisodeCard key={episode.id} episode={episode} />
                ))}
              </div>

              {totalPages > 1 && (
                <div className="mt-8 flex items-center justify-center gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="inline-flex items-center gap-1 rounded-md bg-background-900 border border-primary-900/40 px-4 py-2 text-sm text-foreground-200 hover:bg-background-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-arrow-left-s-line"></i>
                    上一页
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((num) => (
                    <button
                      key={num}
                      type="button"
                      onClick={() => setPage(num)}
                      className={`w-10 h-10 rounded-md text-sm font-medium transition-colors cursor-pointer ${
                        num === currentPage
                          ? 'bg-primary-500 text-background-50'
                          : 'bg-background-900 border border-primary-900/40 text-foreground-200 hover:bg-background-800'
                      }`}
                    >
                      {num}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="inline-flex items-center gap-1 rounded-md bg-background-900 border border-primary-900/40 px-4 py-2 text-sm text-foreground-200 hover:bg-background-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors whitespace-nowrap cursor-pointer"
                  >
                    下一页
                    <i className="ri-arrow-right-s-line"></i>
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}